// What each listed trace cost: its tokens by provider and model, read for the
// traces a list shows and priced in the browser at the catalog's rates, as the
// Overview prices its total (lib/tokenCost.js). A cost is measured when every
// call the trace made reported its tokens and priced, and the trace did not
// start before the window; otherwise its figure is a floor ("$9.60+"). A
// trace nothing priced has no cost, never $0.
import { useEffect, useMemo, useState } from "react";
import { rangeToTimeRange, rowsToObjects, runQuery } from "./analyticsClient.js";
import { MAX_RANGE_DAYS, toApiRange } from "./shell.js";
import { summarizeTokenCost, tokenCostEnabled } from "./tokenCost.js";
import { cacheTokenMetrics, coverageMetrics, inclusiveCacheMetric } from "./tokenVolume.js";

const DAY = 86_400_000;
const MAX_RANGE_MS = MAX_RANGE_DAYS * DAY;
// How far before its window a read looks for a trace's earlier calls.
const LOOKBACK_MS = DAY;
// The most groups one read returns, and the most ids one IN list holds.
const GROUP_CAP = 10_000;
export const IDS_PER_READ = 1000;
const TOKEN_COLUMNS = ["input_tokens", "output_tokens", "cache_read_tokens", "cache_creation_tokens"];

const PU = (name) => ({ stepAlias: "pu", field: `DIMENSION_FIELD_TOKEN_${name}` });
const pred = (field, op, value) => ({ pred: { field, op: `OPERATOR_${op}`, value } });
const and = (a, b) => (a && b ? { andExpr: { children: [a, b] } } : a || b);
// A time as the planner takes one in a predicate.
const stamp = (ms) => new Date(ms).toISOString().replace("T", " ").replace("Z", "");
const lower = (v) => String(v ?? "").trim().toLowerCase();

/**
 * Each of `ids`' tokens by provider and model in `bounds` ({ from, to }, ISO),
 * and the calls each made in the day before it (`calls_before`): a trace
 * already running when the window opened.
 */
export function costSpec({ ids, bounds }) {
  const from = Date.parse(bounds.from);
  const to = Date.parse(bounds.to);
  const start = Math.max(from - LOOKBACK_MS, to - MAX_RANGE_MS + 1);
  const since = pred(PU("OBSERVED_AT"), "GTE", stamp(from));
  const inWindow = (m) => ({ ...m, standard: { ...m.standard, predicate: and(m.standard.predicate, since) } });
  return {
    root: { alias: "pu", entity: "ENTITY_TYPE_TOKEN" },
    timeRange: rangeToTimeRange({ from: new Date(start).toISOString(), to: bounds.to }),
    filter: pred(PU("SESSION_ID"), "IN", ids),
    output: {
      dimensions: [
        { field: PU("SESSION_ID"), outputAlias: "session_id" },
        { field: PU("PROVIDER"), outputAlias: "provider" },
        { field: PU("MODEL"), outputAlias: "model" },
      ],
      metrics: [
        ...[
          { outputAlias: "input_tokens", standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_INPUT_TOKENS" } },
          { outputAlias: "output_tokens", standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" } },
          ...cacheTokenMetrics("pu"),
          ...inclusiveCacheMetric("pu"),
          ...coverageMetrics("pu"),
        ].map(inWindow),
        ...(start < from
          ? [{ outputAlias: "calls_before", standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_CALLS_TOTAL", predicate: pred(PU("OBSERVED_AT"), "LT", stamp(from)) } }]
          : []),
      ],
    },
    orderBy: [{ outputAlias: "session_id", direction: "DIRECTION_ASC" }],
    limit: GROUP_CAP,
  };
}

/**
 * -> { [id]: { rows, truncated, before } }: each trace's token rows by
 * provider and model ([] for none), whether the read reached its group cap,
 * and whether the trace made calls before the window opened.
 */
export async function readTraceCosts({ ids, bounds }, execute = runQuery) {
  const want = [...new Set((ids || []).map(lower).filter(Boolean))].slice(0, IDS_PER_READ);
  if (!want.length) return {};
  const rows = rowsToObjects(await execute(costSpec({ ids: want, bounds })));
  const truncated = rows.length >= GROUP_CAP;
  const out = Object.fromEntries(want.map((id) => [id, { rows: [], truncated, before: false }]));
  for (const r of rows) {
    const entry = out[lower(r.session_id)];
    if (!entry) continue;
    if ((Number(r.calls_before) || 0) > 0) entry.before = true;
    if (r.calls_total == null || Number(r.calls_total) > 0) entry.rows.push(r);
  }
  return out;
}

/**
 * What a trace cost (an entry of readTraceCosts): `usd` at the catalog's
 * rates, null where nothing priced or too little to stand for the trace;
 * `measured` when every call it made, from its first, reported its tokens
 * and priced. A row whose calls reported no tokens prices nothing, never $0.
 */
export function traceCost(entry) {
  const { rows = [], truncated = false, before = false } = entry || {};
  const reported = rows.filter((r) => (r.calls_with_tokens == null ? TOKEN_COLUMNS.some((c) => Number(r[c]) > 0) : Number(r.calls_with_tokens) > 0));
  const priced = summarizeTokenCost(reported);
  if (priced.total == null || priced.suppressTotal) return { usd: null, measured: false };
  const counted = rows.every((r) => r.calls_with_tokens == null || (Number(r.calls_with_tokens) || 0) >= (Number(r.calls_total) || 0));
  return { usd: priced.total, measured: counted && !priced.hasUnpriced && !truncated && !before };
}

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "$654.73", "$9.60+" where some of it could not be priced, "" where none of it could. */
export function costText(cost) {
  if (cost?.usd == null) return "";
  return `${USD.format(cost.usd)}${cost.measured ? "" : "+"}`;
}

/**
 * The costs of `ids` over `range`: id -> { usd, measured } once read (null
 * where the read failed), each id read once per range. Off with the cost
 * estimates' kill switch.
 */
export function useTraceCosts(ids, range) {
  const bounds = useMemo(() => toApiRange(range), [range]);
  const windowKey = bounds ? `${bounds.from}|${bounds.to}` : "";
  const [state, setState] = useState({ windowKey, costs: new Map() });
  const costs = state.windowKey === windowKey ? state.costs : new Map();
  const want = bounds && tokenCostEnabled() ? [...new Set((ids || []).map(lower).filter((id) => id && !costs.has(id)))].slice(0, IDS_PER_READ) : [];
  const key = want.join(",");
  useEffect(() => {
    if (!key || !bounds) return undefined;
    let dead = false;
    const ask = key.split(",");
    const land = (valueOf) => setState((cur) => {
      const base = cur.windowKey === windowKey ? cur.costs : new Map();
      const next = new Map(base);
      for (const id of ask) next.set(id, valueOf(id));
      return { windowKey, costs: next };
    });
    readTraceCosts({ ids: ask, bounds }).then(
      (by) => { if (!dead) land((id) => traceCost(by[id])); },
      () => { if (!dead) land(() => null); },
    );
    return () => { dead = true; };
  }, [key, bounds, windowKey]);
  return costs;
}
